/**
 * PaymentMethodTabs — Segmented control PIX / Boleto / Cartão.
 *
 * Estilo "Modern institutional": container `bg-slate-50 p-1.5 rounded-2xl`,
 * pill ativo branco com ring sutil e bolinha brand. Lazy: o painel só é
 * montado quando a tab é ativada pela primeira vez — depois fica cacheado
 * em memória durante a sessão (a tab persiste em sessionStorage por slug).
 *
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Barcode, CreditCard, QrCode } from 'lucide-react';

export type PaymentMethod = 'pix' | 'boleto' | 'card';

interface Props {
  available: PaymentMethod[];
  slug: string;
  renderPix: () => ReactNode;
  renderBoleto: () => ReactNode;
  renderCard: () => ReactNode;
}

const STORAGE_PREFIX = 'checkout.tab.';
const LABELS: Record<PaymentMethod, { label: string; Icon: typeof QrCode }> = {
  pix: { label: 'PIX', Icon: QrCode },
  boleto: { label: 'Boleto', Icon: Barcode },
  card: { label: 'Cartão', Icon: CreditCard },
};

export function PaymentMethodTabs({
  available,
  slug,
  renderPix,
  renderBoleto,
  renderCard,
}: Props) {
  // Tab inicial: persistida → primeira disponível
  const initialTab: PaymentMethod = useMemo(() => {
    if (typeof window === 'undefined') return available[0] ?? 'pix';
    try {
      const stored = sessionStorage.getItem(STORAGE_PREFIX + slug) as PaymentMethod | null;
      if (stored && available.includes(stored)) return stored;
    } catch {
      /* sessionStorage indisponível — segue com o default */
    }
    return available[0] ?? 'pix';
  }, [available, slug]);

  const [active, setActive] = useState<PaymentMethod>(initialTab);
  // Conjunto de tabs já montadas — garante o lazy mount
  const [mounted, setMounted] = useState<Set<PaymentMethod>>(() => new Set([initialTab]));

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_PREFIX + slug, active);
    } catch {
      /* ignora */
    }
  }, [active, slug]);

  const switchTo = useCallback((m: PaymentMethod) => {
    setActive(m);
    setMounted((prev) => {
      if (prev.has(m)) return prev;
      const next = new Set(prev);
      next.add(m);
      return next;
    });
  }, []);

  if (available.length === 0) return null;

  return (
    <section className="px-3 md:px-9 pt-4 md:pt-8 pb-6 md:pb-12">
      {/* Trilho interno alinhado ao card de resumo (~15% menor que a área útil externa). */}
      <div className="md:max-w-[56.5rem] md:mx-auto">
      {available.length > 1 && (
        <div
          role="tablist"
          aria-label="Forma de pagamento"
          className="bg-slate-50 p-1.5 rounded-2xl flex border border-slate-200/60 mb-4 md:mb-8"
        >
          {available.map((m) => {
            const isActive = m === active;
            const { label, Icon } = LABELS[m];
            return (
              <button
                key={m}
                role="tab"
                aria-selected={isActive}
                type="button"
                onClick={() => switchTo(m)}
                className={
                  'flex-1 flex items-center justify-center gap-2 py-3 md:py-3.5 rounded-xl text-sm font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] ' +
                  (isActive
                    ? 'bg-white shadow-sm text-slate-900 ring-1 ring-slate-900/5'
                    : 'text-slate-500 hover:text-slate-800')
                }
              >
                {isActive && (
                  <span
                    aria-hidden
                    className="w-2 h-2 rounded-full bg-[var(--brand-primary)]"
                  />
                )}
                <Icon className="w-4 h-4 opacity-80" />
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Container dos métodos de pagamento — no mobile usa o próprio envelope; no desktop ganha card branco com sombra */}
      <div className="bg-transparent border-0 p-0 shadow-none md:bg-white md:rounded-3xl md:border md:border-slate-100 md:p-8 md:shadow-xl md:shadow-slate-900/5">
        {/* Lazy mount: cada painel só é montado quando a tab é ativada. */}
        <div hidden={active !== 'pix'} role="tabpanel">
          {mounted.has('pix') && available.includes('pix') ? renderPix() : null}
        </div>
        <div hidden={active !== 'boleto'} role="tabpanel">
          {mounted.has('boleto') && available.includes('boleto') ? renderBoleto() : null}
        </div>
        <div hidden={active !== 'card'} role="tabpanel">
          {mounted.has('card') && available.includes('card') ? renderCard() : null}
        </div>
      </div>
      </div>
    </section>
  );
}
